'use client';

import { type inferParserType, parseAsArrayOf, parseAsInteger, parseAsString, parseAsStringEnum, throttle, useQueryStates } from 'nuqs';
import { useMemo } from 'react';
import { z } from 'zod';
import { generalConfig } from '@sola/config/lib/getters.ts';
import { ChildGenderEnum } from '@sola/schema/dotcom/children/childUtilityInterfaces.ts';
const sacFilterValues = ['orphanedChild', 'hasDisabilities', 'vulnerableExploitation', 'affectedHiv'] as const;
export const SacFiltersEnum = z.enum(sacFilterValues);
export type SacFiltersEnum = z.infer<typeof SacFiltersEnum>;
const {
  sacMinAge,
  sacMaxAge
} = generalConfig;
export const sacQueryParamsSchema = {
  gender: parseAsStringEnum<ChildGenderEnum>(Object.values(ChildGenderEnum)).withDefault(ChildGenderEnum.Either),
  countries: parseAsArrayOf(parseAsString.withDefault('')).withDefault([]),
  ageMin: parseAsInteger.withDefault(sacMinAge),
  ageMax: parseAsInteger.withDefault(sacMaxAge),
  birthDay: parseAsInteger.withDefault(0),
  birthMonth: parseAsInteger.withDefault(0),
  filters: parseAsArrayOf(parseAsString.withDefault(''), ',').withDefault([]),
  page: parseAsInteger.withDefault(0)
};
export type SacQueryParams = inferParserType<typeof sacQueryParamsSchema>;
export const useSacQueryParams = ({
  isDynamic,
  defaults
}: {
  /** When true, reads filters from URL params (dynamic). When false, uses static CMS props. */
  isDynamic?: boolean;
  defaults?: {
    gender?: ChildGenderEnum;
    countries?: string[];
    ageMin?: number;
    ageMax?: number;
    disability?: boolean;
    hiv?: boolean;
    orphan?: boolean;
    highlyVulnerable?: boolean;
  };
}) => {
  const {
    gender: defaultGender,
    countries: defaultCountries,
    ageMin: defaultAgeMin,
    ageMax: defaultAgeMax,
    disability,
    hiv,
    orphan,
    highlyVulnerable
  } = defaults ?? {};
  const countriesKey = defaultCountries?.join(',');
  const defaultFilters = useMemo(() => {
    const f: string[] = [];
    if (disability) {
      f.push('hasDisabilities');
    }
    if (hiv) {
      f.push('affectedHiv');
    }
    if (orphan) {
      f.push('orphanedChild');
    }
    if (highlyVulnerable) {
      f.push('vulnerableExploitation');
    }
    return f.length > 0 ? f : undefined;
  }, [disability, hiv, orphan, highlyVulnerable]);
  const schema = useMemo(() => defaults ? {
    ...sacQueryParamsSchema,
    ...(defaultGender !== undefined && {
      gender: parseAsStringEnum<ChildGenderEnum>(Object.values(ChildGenderEnum)).withDefault(defaultGender)
    }),
    ...(defaultCountries !== undefined && {
      countries: parseAsArrayOf(parseAsString.withDefault('')).withDefault(defaultCountries)
    }),
    ...(defaultAgeMin !== undefined && {
      ageMin: parseAsInteger.withDefault(defaultAgeMin)
    }),
    ...(defaultAgeMax !== undefined && {
      ageMax: parseAsInteger.withDefault(defaultAgeMax)
    }),
    ...(defaultFilters !== undefined && {
      filters: parseAsArrayOf(parseAsString.withDefault(''), ',').withDefault(defaultFilters)
    })
  } : sacQueryParamsSchema, [defaultGender, countriesKey, defaultAgeMin, defaultAgeMax, defaultFilters]);
  const [sacFilters, setSacFilters] = useQueryStates(schema, {
    history: 'replace',
    limitUrlUpdates: isDynamic ? undefined : throttle(Infinity)
  });
  return {
    sacFilters,
    setSacFilters
  };
};